import { useEffect, useMemo, useState } from "react";
import ProjectCommunicationPanel from "./ProjectCommunicationPanel";

const TASK_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_uitvoering", label: "In uitvoering" },
  { value: "wachten", label: "Wachten" },
  { value: "gereed", label: "Gereed" },
];

const TASK_PRIO_OPTIONS = [
  { value: "laag", label: "Laag" },
  { value: "normaal", label: "Normaal" },
  { value: "hoog", label: "Hoog" },
];

export default function ProjectDossierModal({
  open,
  project,
  client,
  workorders,
  opnames,
  tasks,
  calculaties,
  saving,
  onClose,
  onCreateTask,
  onUpdateTaskStatus,
  onSaveNotes,
  onUploadProjectDocument,
  onDeleteProjectDocument,
  onPrepareDossierExport,
  onDownloadDossierExport,
  onDismissDossierExport,
  dossierExportJob = null,
  dossierExportBusy = false,
  communicationAttentionCount = 0,
  onCommunicationAttentionCountChange,
}) {
  const [activeTab, setActiveTab] = useState("overzicht");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskPrio, setTaskPrio] = useState("normaal");
  const [notes, setNotes] = useState(project?.omschrijving || "");
  const [documentCategory, setDocumentCategory] = useState("document");
  const [documentFile, setDocumentFile] = useState(null);
  const [liveCommunicationAttentionCount, setLiveCommunicationAttentionCount] = useState(
    communicationAttentionCount
  );

  useEffect(() => {
    setLiveCommunicationAttentionCount(communicationAttentionCount);
  }, [communicationAttentionCount, project?._id]);

  const contactPersons = useMemo(() => {
    return Array.isArray(client?.contactPersons)
      ? client.contactPersons.filter((item) => item?.active !== false)
      : [];
  }, [client]);

  const documentRows = useMemo(() => {
    return (Array.isArray(workorders) ? workorders : []).flatMap((workorder) => {
      const bijlagen = Array.isArray(workorder?.bijlagen) ? workorder.bijlagen : [];
      return bijlagen.map((bijlage) => ({
        id: `${workorder._id}-${bijlage.bestand || bijlage.naam}`,
        naam: bijlage.naam || "Document",
        type: bijlage.type || "Bijlage",
        werkbon: workorder.werkbonNummer || workorder.nummer || "Werkbon",
        datum: bijlage.uploadedOp || workorder.updatedAt || workorder.createdAt,
      }));
    });
  }, [workorders]);

  const projectDocuments = useMemo(
    () => (Array.isArray(project?.projectDocuments) ? project.projectDocuments : []),
    [project]
  );
  const projectOpnames = useMemo(
    () => (Array.isArray(opnames) ? opnames : []),
    [opnames]
  );

  const exportStatusText = getExportStatusText(dossierExportJob);
  const hasWarnings = Number(dossierExportJob?.warningCount || 0) > 0;
  const canDownloadExport = Boolean(dossierExportJob?.downloadAvailable);
  const isExportRunning = ["queued", "processing"].includes(String(dossierExportJob?.status || ""));
  const tabs = [
    { key: "overzicht", label: "Overzicht" },
    { key: "documenten", label: "Documenten & offertes" },
    { key: "contacten", label: "Contacten & team" },
    {
      key: "communicatie",
      label: "Projectcommunicatie",
      attentionCount: liveCommunicationAttentionCount,
    },
    { key: "taken", label: "Taken & notities" },
  ];

  if (!open || !project) return null;

  const createTask = async (event) => {
    event.preventDefault();
    if (!taskTitle.trim()) return;
    await onCreateTask({ title: taskTitle.trim(), prio: taskPrio });
    setTaskTitle("");
    setTaskPrio("normaal");
  };

  const saveNotes = async () => {
    await onSaveNotes(String(notes || "").trim());
  };

  const uploadProjectDocument = async (event) => {
    event.preventDefault();
    if (!documentFile) return;
    await onUploadProjectDocument({ file: documentFile, categorie: documentCategory });
    setDocumentFile(null);
    setDocumentCategory("document");
  };

  function handleCommunicationAttentionCountChange(nextCount) {
    setLiveCommunicationAttentionCount(nextCount);
    onCommunicationAttentionCountChange?.(nextCount);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Projectdossier</p>
            <h3 className="text-xl font-semibold text-slate-900">{project.naam || project.title || "Project"}</h3>
            <p className="text-sm text-slate-500">{project.projectNummer || project.number || "-"} • {client?.naam || "Geen gekoppelde klant"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={canDownloadExport ? onDownloadDossierExport : onPrepareDossierExport}
              disabled={dossierExportBusy || isExportRunning}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {canDownloadExport
                ? "Downloaden"
                : isExportRunning
                  ? "Dossier wordt samengesteld..."
                  : "Export voorbereiden"}
            </button>
            {dossierExportJob && (
              <button
                type="button"
                onClick={onDismissDossierExport}
                disabled={dossierExportBusy}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                Verbergen
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">Sluiten</button>
          </div>
        </div>

        {dossierExportJob && (
          <div className="border-b border-slate-200 px-5 py-2 text-sm text-slate-600">
            <p>{exportStatusText}</p>
            {dossierExportJob?.expiresAt && canDownloadExport && (
              <p className="text-xs text-slate-500">
                Download beschikbaar tot {formatDateTimeShort(dossierExportJob.expiresAt)}.
              </p>
            )}
            {hasWarnings && (
              <p className="text-xs text-amber-700">
                Export gereed met aandachtspunten. Niet alle onderdelen konden worden meegenomen.
              </p>
            )}
            {dossierExportJob?.errorMessage && (
              <p className="text-xs text-rose-700">{dossierExportJob.errorMessage}</p>
            )}
          </div>
        )}

        <div className="border-b border-slate-200 px-5 py-2">
          <nav className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-3 py-1.5 text-sm ${activeTab === tab.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span className="inline-flex items-center gap-2">
                  <span>{tab.label}</span>
                  {tab.attentionCount > 0 ? (
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">
                      {tab.attentionCount}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "overzicht" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Werkbonnen" value={workorders.length} />
              <MetricCard label="Taken" value={tasks.length} />
              <MetricCard label="Documenten" value={documentRows.length} />
              <MetricCard label="Opnames" value={projectOpnames.length} />
              <MetricCard label="Offertes/Calculaties" value={calculaties.length} />

              <div className="rounded-xl border border-slate-200 p-4 md:col-span-2 lg:col-span-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Samenwerkingscontext</p>
                <p className="mt-2 text-sm text-slate-700">
                  Dit dossier bundelt gedeelde projectinformatie voor werkvoorbereiders en projectleiders.
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 md:col-span-2 lg:col-span-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Opnames / vooronderzoek</p>
                {projectOpnames.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Nog geen opnames gekoppeld aan dit project.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {projectOpnames.slice(0, 8).map((opname) => (
                      <div key={String(opname?._id || opname?.id || "")} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                        <div>
                          <p className="font-medium text-slate-800">{opname?.titel || opname?.title || "Opname"}</p>
                          <p className="text-slate-500">
                            {String(opname?.type || "-")} • {String(opname?.status || "concept")} • {formatDateTimeShort(opname?.datum || opname?.updatedAt || opname?.createdAt)}
                          </p>
                        </div>
                        <a
                          href={`/opname?opnameId=${encodeURIComponent(String(opname?._id || opname?.id || ""))}`}
                          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100"
                        >
                          Open opname
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "documenten" && (
            <div className="space-y-4">
              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Projectdocumenten uploaden</h4>
                <form onSubmit={uploadProjectDocument} className="mt-3 grid gap-2 md:grid-cols-[1fr_200px_auto]">
                  <input
                    type="file"
                    onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                    className="rounded border border-slate-200 px-3 py-2 text-sm"
                    required
                  />
                  <select
                    value={documentCategory}
                    onChange={(event) => setDocumentCategory(event.target.value)}
                    className="rounded border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="offerte">Offerte</option>
                    <option value="contract">Contract</option>
                    <option value="tekening">Tekening</option>
                    <option value="rapport">Rapport</option>
                    <option value="document">Document</option>
                    <option value="overig">Overig</option>
                  </select>
                  <button type="submit" disabled={saving || !documentFile} className="rounded bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60">
                    Uploaden
                  </button>
                </form>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Projectdocumenten (centraal dossier)</h4>
                {projectDocuments.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Nog geen projectdocumenten geüpload.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {projectDocuments.map((doc) => (
                      <div key={doc._id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                        <div>
                          <p className="font-medium text-slate-800">{doc.naam || "Document"}</p>
                          <p className="text-slate-600">{doc.categorie || "document"}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`/uploads/projecten/${doc.bestand}`}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100"
                          >
                            Openen
                          </a>
                          <button
                            type="button"
                            onClick={() => onDeleteProjectDocument(doc)}
                            className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700 hover:bg-rose-100"
                            disabled={saving}
                          >
                            Verwijderen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Offertes / calculaties</h4>
                {calculaties.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Nog geen calculaties of offertes gevonden.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {calculaties.map((item) => (
                      <div key={item._id} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-800">{item.title || "Calculatie"}</p>
                        <p className="text-slate-600">Status: {item.status || "-"} • Versie: {item.version || 1}</p>
                        <p className="text-slate-600">Totaal verkoop: € {Number(item?.totals?.totalSell || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Documenten uit gekoppelde werkbonnen</h4>
                {documentRows.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Nog geen documenten gekoppeld.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {documentRows.map((doc) => (
                      <div key={doc.id} className="rounded-lg border border-slate-100 bg-white p-3 text-sm">
                        <p className="font-medium text-slate-800">{doc.naam}</p>
                        <p className="text-slate-600">{doc.type} • {doc.werkbon}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === "contacten" && (
            <div className="grid gap-4 md:grid-cols-2">
              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Klantcontacten</h4>
                {contactPersons.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Geen actieve contactpersonen.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {contactPersons.map((contact) => (
                      <div key={contact._id} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-800">{contact.name || "Onbekend"}</p>
                        <p className="text-slate-600">{[contact.role, contact.phone, contact.email].filter(Boolean).join(" • ") || "-"}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Projectteam</h4>
                {Array.isArray(project.members) && project.members.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {project.members.map((member) => (
                      <div key={`${member.userId}-${member.role}`} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-800">{member.userId?.naam || member.userId?.email || member.userId || "Gebruiker"}</p>
                        <p className="text-slate-600">Rol: {member.role || "-"}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">Nog geen projectteam gekoppeld.</p>
                )}
              </section>
            </div>
          )}

          {activeTab === "taken" && (
            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Projecttaken</h4>
                <form onSubmit={createTask} className="mt-3 space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <input
                    value={taskTitle}
                    onChange={(event) => setTaskTitle(event.target.value)}
                    placeholder="Nieuwe taak"
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={taskPrio}
                      onChange={(event) => setTaskPrio(event.target.value)}
                      className="rounded border border-slate-200 px-3 py-2 text-sm"
                    >
                      {TASK_PRIO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <button type="submit" disabled={saving} className="rounded bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60">
                      Taak toevoegen
                    </button>
                  </div>
                </form>

                {tasks.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">Nog geen taken op dit project.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {tasks.map((task) => (
                      <div key={task._id} className="rounded-lg border border-slate-100 p-3 text-sm">
                        <p className="font-medium text-slate-800">{task.title}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-slate-600">Prio: {task.prio || "normaal"}</span>
                          <select
                            value={task.status || "open"}
                            onChange={(event) => onUpdateTaskStatus(task, event.target.value)}
                            className="rounded border border-slate-200 px-2 py-1 text-xs"
                            disabled={saving}
                          >
                            {TASK_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Interne notities project</h4>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={12}
                  className="mt-3 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Notities, afspraken en overdrachtsinfo"
                />
                <div className="mt-2 flex justify-end">
                  <button type="button" onClick={saveNotes} className="rounded bg-slate-900 px-3 py-2 text-sm text-white" disabled={saving}>
                    Notities opslaan
                  </button>
                </div>
              </section>
            </div>
          )}

          {activeTab === "communicatie" && (
              <ProjectCommunicationPanel
                project={project}
                client={client}
                initialAttentionCount={liveCommunicationAttentionCount}
                onAttentionCountChange={handleCommunicationAttentionCountChange}
              />
          )}
        </div>
      </div>
    </div>
  );
}

function formatDateTimeShort(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getExportStatusText(job) {
  const status = String(job?.status || "").toLowerCase();
  const step = String(job?.step || "").toLowerCase();

  if (!status) return "Export voorbereiden";
  if (status === "queued") return "Export voorbereiden";
  if (status === "processing") {
    if (step === "collecting_data") return "Dossier wordt samengesteld";
    if (step === "generating_pdfs") return "Werkbon-pdf’s worden voorbereid";
    if (step === "packing_files") return "Bestanden worden verzameld";
    if (step === "finalizing") return "Export wordt afgerond";
    return "Dossier wordt samengesteld";
  }
  if (status === "done") return "Export gereed";
  if (status === "failed") return "Export mislukt";
  if (status === "expired") return "Export verlopen";
  return "Exportstatus onbekend";
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}
